import * as Sentry from "@sentry/nextjs";
import { maxBy } from "es-toolkit";
import { makeAutoObservable, reaction, runInAction } from "mobx";

import {
  checkSchoolDomain,
  createUserEmail,
  getApiErrorMessage,
  isApiError,
  joinCourses,
  listCourses,
  listSchools,
  showCourse,
  showSchool,
  submitCourse,
  submitSchool,
  verifyUserEmail,
} from "@/api";
import {
  getCurrentTerm,
  getLatestTerm,
  getSeasonTermLabel,
} from "@/components/student-onboarding/getCurrentTerm";
import {
  getEmailDomain,
  getSchoolDomains,
  isSchoolAddress,
} from "@/components/student-onboarding/schoolDomains";
import { withFromApp } from "@/components/student-onboarding/useIsOpenedFromApp";
import { readPendingCourse, rememberPendingCourse } from "@/store/pendingCourse";
import { teamPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";

import type {
  Course,
  CurrentUser,
  JoinCourseResult,
  JoinCourseResultStatus,
  School,
  SchoolShort,
  Term,
  TeamCoursePreview,
  TeamShort,
} from "@/api";
import type { PendingCourse } from "@/store/pendingCourse";
import type { RootStore } from "@/store/RootStore";
import type { IReactionDisposer } from "mobx";

const ABORT_REASON = "Student onboarding request dropped";

const JOINED_STATUSES = new Set<JoinCourseResultStatus>(["joined", "already-joined"]);

type Affiliation = { schools: School[]; found: number };

function getJoinedTeams(results: JoinCourseResult[]): TeamShort[] {
  return results.flatMap(({ status, team }) => (JOINED_STATUSES.has(status) && team ? [team] : []));
}

function getVerifiedEmailsKey(user: CurrentUser | null | undefined): string | null {
  if (!user) return null;

  return user.emails
    .filter((email) => email.verified)
    .map((email) => email.email)
    .sort()
    .join(",");
}

export type DomainRejectionMessageKey = "publicDomain" | "relayDomain";

const DOMAIN_REJECTION_KEYS = {
  public_email_domain: "publicDomain",
  relay_email_domain: "relayDomain",
} as const;

function isCourseJoined(results: JoinCourseResult[], courseUid: string | undefined): boolean {
  return results.some((row) => row.courseUid === courseUid && JOINED_STATUSES.has(row.status));
}

export type JoinFailureMessageKey =
  | `auth.studentOnboarding.selectCourse.${Extract<
      keyof IntlMessages["auth"]["studentOnboarding"]["selectCourse"],
      "joinFailedNotAffiliated" | "joinFailed"
    >}`
  | `doc.coursePlaceholder.${Extract<keyof IntlMessages["doc"]["coursePlaceholder"], "joinFailed">}`;

function getJoinFailureMessageKey(results: JoinCourseResult[]): JoinFailureMessageKey {
  return results.some((result) => result.status === "not-affiliated")
    ? "auth.studentOnboarding.selectCourse.joinFailedNotAffiliated"
    : "auth.studentOnboarding.selectCourse.joinFailed";
}

export class StudentOnboardingStore {
  rootStore: RootStore;

  schools: School[] = [];

  schoolsFound = 0;

  schoolsPage = 1;

  schoolQuery = "";

  isSearchingSchools = false;

  isFetchingMoreSchools = false;

  school: School | null = null;

  /** Name the student typed for a school the directory lacks, until it exists. */
  pendingSchoolName = "";

  /**
   * Schools already on the submitted address's domain, accepted ones first.
   */
  domainSchools: SchoolShort[] = [];

  domainRejectionMessageKey: DomainRejectionMessageKey | null = null;

  isSubmittingEmail = false;

  /**
   * Stays set after a successful submit until the step is left, so the button
   * stays busy and the page's missing-email guard holds off while it navigates.
   */
  isSubmittingSchool = false;

  isSubmittingCourse = false;

  /** Kept apart from `error`, so a refusal shows in the add-course dialog only. */
  courseSubmitError = "";

  isVerifyingEmail = false;

  courses: Course[] = [];

  coursesFound = 0;

  coursesPage = 1;

  pinnedCourse: Course | null = null;

  private rememberedCourse: PendingCourse | null = null;

  termUid: string | null = null;

  submissionTermUid: string | null = null;

  courseQuery = "";

  isSearchingCourses = false;

  isFetchingMoreCourses = false;

  isJoiningCourses = false;

  joinFailureMessageKey: JoinFailureMessageKey | null = null;

  error = "";

  emailDraft = "";

  private schoolsAbortController: AbortController | null = null;

  private coursesAbortController: AbortController | null = null;

  /**
   * Bumped by leaveStep. A step's request reads everything it navigates by
   * before its first await, and drops the navigation if the visit changed: a
   * late response must not pull the student back, or into another school.
   */
  private stepVisit = 0;

  private isAffiliatedFilterDismissed = false;

  private affiliation: Affiliation | undefined = undefined;

  private affiliationRequest: ReturnType<typeof listSchools> | null = null;

  private verifiedEmailsReactionDispose?: IReactionDisposer;

  private selectedCourses = new Set<string>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  lazyEnable(): void {
    this.verifiedEmailsReactionDispose = reaction(
      () => getVerifiedEmailsKey(this.rootStore.auth.currentUser),
      () => this.resetAffiliation(),
    );
  }

  get isAffiliationKnown(): boolean {
    return this.affiliation !== undefined;
  }

  get isAffiliated(): boolean {
    return (this.affiliation?.found ?? 0) > 0;
  }

  private get onlyOneAffiliatedSchool(): School | null {
    if (this.affiliation?.found === 1) return this.affiliation.schools[0];

    return null;
  }

  get hasSeveralAffiliatedSchools(): boolean {
    return this.isAffiliated && !this.onlyOneAffiliatedSchool;
  }

  get isSchoolSearchAffiliatedOnly(): boolean {
    return this.hasSeveralAffiliatedSchools && !this.isAffiliatedFilterDismissed;
  }

  private isAffiliatedWith(school: School): boolean {
    if (getSchoolDomains(school).length === 0) return true;

    const emails = this.rootStore.auth.currentUser?.emails ?? [];
    return emails.some((row) => row.verified && isSchoolAddress(school, row.email));
  }

  setEmailDraft(email: string): void {
    this.emailDraft = email;
    this.domainSchools = [];
    this.domainRejectionMessageKey = null;
  }

  get isSingleSelect(): boolean {
    return this.school !== null && this.school.kind !== "courses";
  }

  get hasCourseFilters(): boolean {
    return this.school !== null && this.school.kind !== "high_school";
  }

  get hasAllTermsOption(): boolean {
    return !this.isSingleSelect;
  }

  setPendingSchool(name: string): void {
    if (this.pendingSchoolName === name && !this.school) return;

    this.pendingSchoolName = name;
    this.school = null;
  }

  get schoolName(): string {
    return this.school?.name ?? this.pendingSchoolName;
  }

  private get stepQuery(): string {
    return this.school
      ? `school=${this.school.uid}`
      : `newSchool=${encodeURIComponent(this.pendingSchoolName)}`;
  }

  get hasMoreSchools(): boolean {
    return this.schools.length < this.schoolsFound;
  }

  get hasMoreCourses(): boolean {
    return this.courses.length < this.coursesFound;
  }

  get isSchoolRemembered(): boolean {
    return this.rememberedCourse !== null;
  }

  async searchSchools(query: string, signal?: AbortSignal): Promise<void> {
    this.schoolQuery = query.trim();
    await this.requestSchools(1, signal);
  }

  async fetchMoreSchools(signal?: AbortSignal): Promise<boolean> {
    if (!this.hasMoreSchools || this.isSearchingSchools || this.isFetchingMoreSchools) return false;

    return this.requestSchools(this.schoolsPage + 1, signal);
  }

  private async requestSchools(page: number, signal?: AbortSignal): Promise<boolean> {
    this.schoolsAbortController?.abort(ABORT_REASON);
    if (page === 1 && !this.schoolQuery && this.isSchoolSearchAffiliatedOnly) {
      this.showAffiliatedSchools();
      return true;
    }

    const abortController = new AbortController();
    this.schoolsAbortController = abortController;
    signal?.addEventListener("abort", () => abortController.abort(), { once: true });
    const isFirstPage = page === 1;
    this.isSearchingSchools = isFirstPage;
    this.isFetchingMoreSchools = !isFirstPage;
    this.error = "";

    try {
      const { schools, found } = await listSchools(
        {
          query: this.schoolQuery || undefined,
          affiliated: this.isSchoolSearchAffiliatedOnly || undefined,
          page,
        },
        { signal: abortController.signal, skipCache: true },
      );
      if (!schools) return false;

      runInAction(() => {
        if (isFirstPage) {
          this.schools = schools;
        } else {
          this.schools.push(...schools);
        }
        this.schoolsFound = found;
        this.schoolsPage = page;
        this.isSearchingSchools = false;
        this.isFetchingMoreSchools = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        if (isFirstPage) {
          this.schools = [];
          this.schoolsFound = 0;
        }
        this.isSearchingSchools = false;
        this.isFetchingMoreSchools = false;
        this.error = getApiErrorMessage(err);
      });
      Sentry.captureException(err);
      return false;
    }
  }

  selectSchool(school: School, { isFromApp = false } = {}): void {
    trackEvent("student_school_selected", { school_uid: school.uid });
    if (this.isAffiliatedWith(school)) {
      this.goToCourses(school, { isFromApp });
    } else {
      this.goToVerification(school.uid, { isFromApp });
    }
  }

  /** Starts the flow for a school the search didn't find, named by the query. */
  selectPendingSchool(name: string, { isFromApp = false } = {}): void {
    trackEvent("student_school_suggested", { query: name });
    this.setPendingSchool(name);
    this.rootStore.router?.push(
      withFromApp(`/verify-school-email?newSchool=${encodeURIComponent(name)}`, isFromApp),
    );
  }

  async joinCourseFromDocument(course: TeamCoursePreview, returnTo: string): Promise<void> {
    this.isJoiningCourses = true;
    this.joinFailureMessageKey = null;

    try {
      const { results = [] } = await joinCourses({ courseUids: [course.uid] });
      const status = results[0]?.status;

      if (status && JOINED_STATUSES.has(status)) {
        trackEvent("student_courses_joined", { count: 1 });
        window.location.reload();
        return;
      }

      if (status === "not-affiliated") {
        rememberPendingCourse(course);
        this.rootStore.signInFlow.setRedirectTo(returnTo);
        runInAction(() => {
          this.isJoiningCourses = false;
        });
        this.goToVerification(course.schoolUid, { isFromApp: true });
        return;
      }
    } catch (err) {
      Sentry.captureException(err);
    }

    runInAction(() => {
      this.joinFailureMessageKey = "doc.coursePlaceholder.joinFailed";
      this.isJoiningCourses = false;
    });
  }

  /**
   * Asks for the affiliated schools, one page only: the count tells
   * whether the student has none, one or several, and a district-wide domain can
   * cover a thousand schools, so the list is never loaded whole.
   */
  async loadAffiliation(): Promise<void> {
    if (this.affiliation) return;

    const request = (this.affiliationRequest ??= listSchools(
      { affiliated: true },
      { skipCache: true },
    ));
    try {
      const { schools = [], found = 0 } = await request;
      if (this.affiliationRequest !== request) return;

      runInAction(() => {
        this.affiliation = { schools, found };
      });
    } catch (err) {
      if (this.affiliationRequest === request) {
        Sentry.captureException(err);
        runInAction(() => {
          this.affiliationRequest = null;
        });
      }
    }
  }

  private resetAffiliation(): void {
    this.affiliationRequest = null;
    this.affiliation = undefined;
  }

  /**
   * Skips the school picker when the school is already known: the one behind the class
   * the student came for, or else the only one the student is affiliated with. With
   * several affiliated schools the picker stays, narrowed down to them. A remembered
   * school that fails to load (gone, or a request failure) is treated as not remembered.
   */
  async skipSchoolStep(signal?: AbortSignal, { isFromApp = false } = {}): Promise<boolean> {
    const rememberedSchoolUid = readPendingCourse()?.schoolUid;
    if (rememberedSchoolUid) {
      const school = await this.loadSchool(rememberedSchoolUid, signal);
      if (signal?.aborted) return false;

      if (school) {
        if (this.isAffiliatedWith(school)) {
          this.goToCourses(school, { isFromApp });
        } else {
          this.goToVerification(school.uid, { isFromApp });
        }
        return true;
      }
    }

    await this.loadAffiliation();
    if (signal?.aborted) return false;

    if (this.onlyOneAffiliatedSchool) {
      this.goToCourses(this.onlyOneAffiliatedSchool, { isFromApp });
      return true;
    }

    if (this.isSchoolSearchAffiliatedOnly) this.narrowSchoolsToAffiliated();
    return false;
  }

  private narrowSchoolsToAffiliated(): void {
    this.schoolsAbortController?.abort(ABORT_REASON);
    this.showAffiliatedSchools();
  }

  private showAffiliatedSchools(): void {
    if (!this.affiliation) return;

    this.schools = [...this.affiliation.schools];
    this.schoolsFound = this.affiliation.found;
    this.schoolsPage = 1;
    this.isSearchingSchools = false;
    this.isFetchingMoreSchools = false;
    this.error = "";
  }

  showAllSchools(): void {
    this.isAffiliatedFilterDismissed = true;
    void this.searchSchools(this.schoolQuery);
  }

  async openJoinCourse(): Promise<void> {
    await this.loadAffiliation();

    if (this.onlyOneAffiliatedSchool) {
      this.goToCourses(this.onlyOneAffiliatedSchool, { isFromApp: true });
    } else {
      this.rootStore.router?.push("/select-school?from=app");
    }
  }

  private goToCourses(school: School, { isFromApp = false } = {}): void {
    this.school = school;
    this.rootStore.router?.push(withFromApp(`/select-course?school=${school.uid}`, isFromApp));
  }

  private goToVerification(schoolUid: string, { isFromApp = false } = {}): void {
    this.rootStore.router?.push(withFromApp(`/verify-school-email?school=${schoolUid}`, isFromApp));
  }

  async loadSchool(schoolUid: string, signal?: AbortSignal): Promise<School | null> {
    if (this.school?.uid === schoolUid) return this.school;

    this.domainSchools = [];
    this.domainRejectionMessageKey = null;

    const alreadyLoaded = this.schools.find((row) => row.uid === schoolUid);
    if (alreadyLoaded) {
      this.school = alreadyLoaded;
      return alreadyLoaded;
    }

    try {
      const { school } = await showSchool(schoolUid, { signal });
      if (!school) return null;

      runInAction(() => {
        this.school = school;
      });
      return school;
    } catch (err) {
      runInAction(() => {
        this.error = getApiErrorMessage(err);
      });
      Sentry.captureException(err);
      return null;
    }
  }

  async submitSchoolEmail(email: string, { isFromApp = false } = {}): Promise<void> {
    this.isSubmittingEmail = true;
    this.error = "";
    const isDomainAcknowledged = this.hasDomainSuggestions;
    this.domainSchools = [];
    this.domainRejectionMessageKey = null;

    const visit = this.stepVisit;
    const { stepQuery, emailDraft } = this;
    const stepAfterVerification = this.captureStepAfterVerification();

    try {
      if (!this.school && !isDomainAcknowledged && !(await this.canSubmitDomain(email))) return;
      // An address edited while the domain was being checked is a new one to check.
      if (visit !== this.stepVisit || this.emailDraft !== emailDraft) return;

      const { user } = await createUserEmail({ email });
      const created = user?.emails.find((row) => row.email === email.trim().toLowerCase());
      if (!created) {
        throw new Error("The created email is missing from the response");
      }
      if (visit !== this.stepVisit) return;

      // An address the user already verified is returned as-is, with no code mailed.
      const next = created.verified
        ? stepAfterVerification(created.uid)
        : `/verify-school-email?${stepQuery}&emailUid=${created.uid}`;
      this.rootStore.router?.push(withFromApp(next, isFromApp));
    } catch (err) {
      runInAction(() => {
        this.error = getApiErrorMessage(err);
      });
    } finally {
      runInAction(() => {
        this.isSubmittingEmail = false;
      });
    }
  }

  /**
   * The address a student gives for a school we don't have becomes that
   * school's domain, so it is checked before a code goes out — a code mailed
   * to a domain that can never be one leads nowhere.
   */
  private async canSubmitDomain(email: string): Promise<boolean> {
    const { schools, rejection } = await checkSchoolDomain({ domain: getEmailDomain(email) });
    if (schools.length === 0 && !rejection) return true;

    runInAction(() => {
      this.isSubmittingEmail = false;
      this.domainSchools = schools;
      this.domainRejectionMessageKey = rejection ? DOMAIN_REJECTION_KEYS[rejection] : null;
    });
    return false;
  }

  /** Schools on the domain were offered, and the address may still add a new one. */
  get hasDomainSuggestions(): boolean {
    return this.domainSchools.length > 0;
  }

  private captureStepAfterVerification(): (userEmailUid: string) => string {
    const { school, stepQuery } = this;
    return (userEmailUid) =>
      school
        ? `/select-course?school=${school.uid}`
        : `/add-school?${stepQuery}&emailUid=${userEmailUid}`;
  }

  /** The verified email's domain becomes the new school's first domain. */
  async submitSchool(
    name: string,
    countryCode: string,
    emailUid: string,
    { isFromApp = false } = {},
  ): Promise<void> {
    this.isSubmittingSchool = true;
    this.error = "";
    const visit = this.stepVisit;

    try {
      const { school } = await submitSchool({
        name: name.trim(),
        countryCode,
        emailUid,
        // The courses step needs a term to add courses to, and a new school has none.
        terms: [{ label: getSeasonTermLabel() }],
      });
      if (!school) {
        throw new Error("The submitted school is missing from the response");
      }
      if (visit !== this.stepVisit) return;

      runInAction(() => {
        this.school = school;
        this.pendingSchoolName = "";
      });
      trackEvent("student_school_submitted", { school_uid: school.uid });
      this.rootStore.router?.push(withFromApp(`/add-course?school=${school.uid}`, isFromApp));
    } catch (err) {
      runInAction(() => {
        this.isSubmittingSchool = false;
        this.error = getApiErrorMessage(err);
      });
    }
  }

  async verifySchoolEmail(
    userEmailUid: string,
    code: string,
    { isFromApp = false } = {},
  ): Promise<void> {
    this.isVerifyingEmail = true;
    this.error = "";
    const visit = this.stepVisit;
    const stepAfterVerification = this.captureStepAfterVerification();

    try {
      await verifyUserEmail(userEmailUid, { code: code.trim() });
      if (visit !== this.stepVisit) return;

      this.rootStore.router?.push(withFromApp(stepAfterVerification(userEmailUid), isFromApp));
    } catch (err) {
      runInAction(() => {
        this.error = getApiErrorMessage(err);
      });
    } finally {
      runInAction(() => {
        this.isVerifyingEmail = false;
      });
    }
  }

  async resendVerification(email: string): Promise<boolean> {
    this.error = "";
    try {
      await createUserEmail({ email });
      return true;
    } catch (err) {
      runInAction(() => {
        this.error = getApiErrorMessage(err);
      });
      return false;
    }
  }

  async openCourses(schoolUid: string, signal?: AbortSignal): Promise<void> {
    const remembered = readPendingCourse();
    this.rememberedCourse = remembered?.schoolUid === schoolUid ? remembered : null;
    await this.loadSchool(schoolUid, signal);
    if (signal?.aborted) return;

    runInAction(() => {
      this.termUid = this.defaultTermUid();
    });
    await Promise.all([
      this.pinPendingCourse(signal),
      this.searchCourses(signal),
      this.loadAffiliation(),
    ]);
  }

  private defaultTermUid(): string | null {
    if (!this.school || !this.hasCourseFilters) return null;

    const { terms } = this.school;
    const fallback = this.hasAllTermsOption ? null : getLatestTerm(terms);
    return (getCurrentTerm(terms) ?? fallback)?.uid ?? null;
  }

  private async pinPendingCourse(signal?: AbortSignal): Promise<void> {
    if (!this.rememberedCourse) return;

    try {
      const { course } = await showCourse(this.rememberedCourse.courseUid, {
        signal,
        skipCache: true,
      });
      if (!course || signal?.aborted) return;

      runInAction(() => {
        this.pinnedCourse = course;
        this.selectedCourses.add(course.uid);
      });
    } catch (err) {
      if (!(isApiError(err) && err.response?.status === 404)) Sentry.captureException(err);
    }
  }

  setCourseQuery(query: string): void {
    this.courseQuery = query;
    void this.searchCourses();
  }

  selectTerm(termUid: string | null): void {
    this.termUid = termUid;
    if (this.isSingleSelect) this.selectedCourses.clear();
    void this.searchCourses();
  }

  selectSubmissionTerm(termUid: string | null): void {
    this.submissionTermUid = termUid;
  }

  /** The term a course added on the "add your courses" step goes into. */
  get submissionTerm(): Term | null {
    const terms = this.school?.terms ?? [];
    return (
      terms.find((term) => term.uid === this.submissionTermUid) ??
      getCurrentTerm(terms) ??
      terms[0] ??
      null
    );
  }

  async searchCourses(signal?: AbortSignal): Promise<void> {
    await this.requestCourses(1, signal);
  }

  async fetchMoreCourses(signal?: AbortSignal): Promise<boolean> {
    if (!this.hasMoreCourses || this.isSearchingCourses || this.isFetchingMoreCourses) return false;

    return this.requestCourses(this.coursesPage + 1, signal);
  }

  private async requestCourses(page: number, signal?: AbortSignal): Promise<boolean> {
    const schoolUid = this.school?.uid;
    if (!schoolUid) return false;

    this.coursesAbortController?.abort(ABORT_REASON);

    const abortController = new AbortController();
    this.coursesAbortController = abortController;
    signal?.addEventListener("abort", () => abortController.abort(), { once: true });
    const isFirstPage = page === 1;
    this.isSearchingCourses = isFirstPage;
    this.isFetchingMoreCourses = !isFirstPage;
    this.error = "";

    try {
      const { courses, found } = await listCourses(
        {
          school_uid: schoolUid,
          term_uid: this.termUid ?? undefined,
          query: this.courseQuery.trim() || undefined,
          order: "subject",
          page,
        },
        { signal: abortController.signal, skipCache: true },
      );
      if (!courses) return false;

      runInAction(() => {
        if (isFirstPage) {
          this.courses = courses;
        } else {
          this.courses.push(...courses);
        }
        this.coursesFound = found;
        this.coursesPage = page;
        this.isSearchingCourses = false;
        this.isFetchingMoreCourses = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        if (isFirstPage) {
          this.courses = [];
          this.coursesFound = 0;
        }
        this.isSearchingCourses = false;
        this.isFetchingMoreCourses = false;
        this.error = getApiErrorMessage(err);
      });
      Sentry.captureException(err);
      return false;
    }
  }

  /**
   * The course lands on review and is selected straight away: the student added
   * it in order to join it.
   */
  async submitCourse(name: string, code: string): Promise<boolean> {
    const schoolUid = this.school?.uid;
    if (!schoolUid) return false;

    this.isSubmittingCourse = true;
    this.courseSubmitError = "";
    const visit = this.stepVisit;

    try {
      const { course } = await submitCourse({
        schoolUid,
        name: name.trim(),
        code: code.trim() || undefined,
        termUid: this.submissionTerm?.uid,
      });
      if (!course) {
        throw new Error("The submitted course is missing from the response");
      }
      if (visit !== this.stepVisit) return false;

      runInAction(() => {
        this.courses.push(course);
        this.selectedCourses.add(course.uid);
        this.isSubmittingCourse = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.isSubmittingCourse = false;
        this.courseSubmitError = getApiErrorMessage(err);
      });
      return false;
    }
  }

  clearCourseSubmitError(): void {
    this.courseSubmitError = "";
  }

  toggleCourse(courseUid: string): void {
    if (this.selectedCourses.has(courseUid)) {
      this.selectedCourses.delete(courseUid);
    } else if (this.isSingleSelect) {
      this.selectedCourses.clear();
      this.selectedCourses.add(courseUid);
    } else {
      this.selectedCourses.add(courseUid);
    }
  }

  isCourseSelected(courseUid: string): boolean {
    return this.selectedCourses.has(courseUid);
  }

  get selectedCourseUids(): string[] {
    return Array.from(this.selectedCourses);
  }

  get hasSelectedCourses(): boolean {
    return this.selectedCourses.size > 0;
  }

  async joinSelectedCourses({ isFromApp = false } = {}): Promise<void> {
    const courseUids = this.selectedCourseUids;
    if (courseUids.length === 0) return;

    this.isJoiningCourses = true;
    this.joinFailureMessageKey = null;
    this.error = "";

    try {
      const { results = [] } = await joinCourses({ courseUids });
      const joinedTeams = getJoinedTeams(results);
      const mostPopulatedTeam = maxBy(joinedTeams, (team) => team.membersCount);

      if (mostPopulatedTeam) {
        trackEvent("student_courses_joined", { count: joinedTeams.length });
        const { signInFlow, teams } = this.rootStore;
        const isSignupInProgress = signInFlow.hasPendingRedirect() && !isFromApp;
        if (!isCourseJoined(results, readPendingCourse()?.courseUid)) {
          signInFlow.setRedirectTo(
            teamPageUrl({ teamPermalink: mostPopulatedTeam.permalink, signup: isSignupInProgress }),
          );
        }
        await teams.fetchTeams().catch(() => {});
        signInFlow.resetRedirectAfterLoginGuard();
        await signInFlow.redirectAfterLogin();
        return;
      }

      runInAction(() => {
        this.isJoiningCourses = false;
        this.joinFailureMessageKey = getJoinFailureMessageKey(results);
      });
    } catch (err) {
      runInAction(() => {
        this.isJoiningCourses = false;
        this.error = getApiErrorMessage(err);
      });
      Sentry.captureException(err);
    }
  }

  leaveStep(): void {
    this.stepVisit += 1;
    this.coursesAbortController?.abort(ABORT_REASON);
    this.coursesAbortController = null;
    this.courses = [];
    this.coursesFound = 0;
    this.coursesPage = 1;
    this.pinnedCourse = null;
    this.rememberedCourse = null;
    this.termUid = null;
    this.submissionTermUid = null;
    this.courseQuery = "";
    this.selectedCourses = new Set();
    this.isSearchingCourses = false;
    this.isFetchingMoreCourses = false;
    this.isSubmittingEmail = false;
    this.isSubmittingSchool = false;
    this.isSubmittingCourse = false;
    this.courseSubmitError = "";
    this.isVerifyingEmail = false;
    this.isJoiningCourses = false;
    this.domainSchools = [];
    this.domainRejectionMessageKey = null;
    this.joinFailureMessageKey = null;
    this.isAffiliatedFilterDismissed = false;
    this.error = "";
  }

  dispose(): void {
    this.verifiedEmailsReactionDispose?.();
    this.leaveStep();
    this.schoolsAbortController?.abort(ABORT_REASON);
    this.schoolsAbortController = null;
    this.schools = [];
    this.schoolsFound = 0;
    this.schoolsPage = 1;
    this.schoolQuery = "";
    this.isSearchingSchools = false;
    this.isFetchingMoreSchools = false;
    this.school = null;
    this.resetAffiliation();
    this.pendingSchoolName = "";
    this.emailDraft = "";
  }
}
